import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Minus, Plus, ShoppingBag, X, Coffee, Croissant, Heart, Star, Trash2, Bookmark } from "lucide-react";
import PhoneShell from "../components/PhoneShell";
import LoyaltyDots from "../components/LoyaltyDots";
import {
    getPhone,
    addToCart,
    getCart,
    cartCount,
    cartSubtotal,
    defaultCustomizations,
    MILK_OPTIONS,
    milkUpcharge,
    customLabel,
} from "../lib/session";
import { api } from "../lib/api";
import { toast } from "sonner";

const sizeLabel = { S: "Small", L: "Large" };

const Menu = () => {
    const nav = useNavigate();
    const { slug } = useParams();
    const ctx = useOutletContext() || {};
    const cafeName = ctx.tenant?.name || "Coffeein7";
    const phone = getPhone();
    const [menu, setMenu] = useState({ coffee: [], food: [] });
    const [loyalty, setLoyalty] = useState({ stamps: 0, free_coffee_available: false });
    const [cart, setCartState] = useState(getCart());
    const [selected, setSelected] = useState(null); // {item, kind}
    const [selectedMods, setSelectedMods] = useState([]);
    const [size, setSize] = useState("S");
    const [qty, setQty] = useState(1);
    const [custom, setCustom] = useState(defaultCustomizations());
    const [saveFav, setSaveFav] = useState(false);
    const [favourites, setFavourites] = useState([]);

    const loadFavs = () => {
        if (!phone) return;
        api.get(`/favourites/${phone}`).then((r) => setFavourites(r.data.favourites || []));
    };

    useEffect(() => {
        if (!phone) {
            nav(`/${slug}`);
            return;
        }
        api.get("/menu").then((r) => setMenu(r.data));
        api.get(`/loyalty/${phone}`).then((r) => setLoyalty(r.data));
        loadFavs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phone, nav, slug]);

    const openItem = (item, kind) => {
        if (item.sold_out) {
            toast.error(`${item.name} is sold out today`);
            return;
        }
        setSelected({ item, kind });
        setSize("S");
        setQty(1);
        setCustom(defaultCustomizations());
        setSaveFav(false);
        setSelectedMods([]);
    };
    const closeSheet = () => setSelected(null);

    const modifierList = selected?.item?.modifiers || [];
    const modUpcharge = useMemo(
        () =>
            modifierList
                .filter((m) => selectedMods.includes(m.id))
                .reduce((s, m) => s + (Number(m.price_delta) || 0), 0),
        [modifierList, selectedMods]
    );

    const currentPrice = useMemo(() => {
        if (!selected) return 0;
        const base = selected.kind === "coffee" ? selected.item.sizes[size] : selected.item.price;
        const milk = selected.kind === "coffee" ? milkUpcharge(custom.milk) : 0;
        return base + milk + modUpcharge;
    }, [selected, size, custom, modUpcharge]);

    const confirmAdd = async () => {
        if (!selected) return;
        const { item, kind } = selected;
        const price = currentPrice;
        const customizations = kind === "coffee" ? custom : null;
        const newCart = addToCart({
            id: item.id,
            name: item.name,
            kind,
            size: kind === "coffee" ? size : null,
            qty,
            price,
            customizations,
            modifier_ids: selectedMods,
            modifier_snapshot: modifierList.filter((m) => selectedMods.includes(m.id)),
        });
        setCartState(newCart);

        if (saveFav && phone) {
            try {
                await api.post(`/favourites/${phone}`, {
                    item_id: item.id,
                    name: item.name,
                    kind,
                    size: kind === "coffee" ? size : null,
                    price,
                    customizations,
                    label: kind === "coffee" ? customLabel(customizations) : "",
                });
                loadFavs();
                toast.success("Saved to your usuals");
            } catch {
                toast.error("Couldn't save favourite");
            }
        }
        closeSheet();
    };

    const addFavToCart = (fav) => {
        const newCart = addToCart({
            id: fav.item_id,
            name: fav.name,
            kind: fav.kind,
            size: fav.size || null,
            qty: 1,
            price: fav.price,
            customizations: fav.customizations || null,
        });
        setCartState(newCart);
        toast.success(`Added ${fav.name}`);
    };

    const removeFav = async (fav) => {
        try {
            await api.delete(`/favourites/${phone}/${fav.id}`);
            loadFavs();
        } catch {
            toast.error("Couldn't remove");
        }
    };

    const count = cartCount(cart);
    const subtotal = cartSubtotal(cart);
    const toNext = Math.max(0, 10 - loyalty.stamps);

    return (
        <PhoneShell>
            <div className="relative pb-32">
                {/* Header */}
                <div className="sticky top-0 z-20 bg-[#F9F6F0]/95 backdrop-blur px-5 pt-6 pb-4 border-b border-[#E8DCCB]/60">
                    <div className="flex items-center justify-between">
                        <button
                            data-testid="back-btn"
                            onClick={() => nav(`/${slug}`)}
                            className="h-10 w-10 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center active:scale-95 transition-transform"
                        >
                            <ArrowLeft className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                        <div className="text-center">
                            <div className="font-display text-2xl font-black leading-none">
                                {cafeName}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#8C7A72] mt-1">
                                ···{phone.slice(-4)}
                            </div>
                        </div>
                        <div className="w-10" />
                    </div>

                    {/* Loyalty banner */}
                    <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-4 rounded-2xl bg-white border border-[#E8DCCB] p-3 flex items-center justify-between"
                        data-testid="loyalty-banner"
                    >
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-[#8C7A72] font-semibold">
                                Your card
                            </div>
                            {loyalty.free_coffee_available ? (
                                <div
                                    className="mt-1 font-display font-bold text-[#FF4F18] text-lg"
                                    data-testid="free-coffee-ready"
                                >
                                    Free coffee ready
                                </div>
                            ) : (
                                <div
                                    className="mt-1 font-display font-bold text-[#2C1A12] text-lg"
                                    data-testid="loyalty-progress-text"
                                >
                                    {toNext} away from free
                                </div>
                            )}
                        </div>
                        <LoyaltyDots stamps={loyalty.stamps} />
                    </motion.div>
                </div>

                {/* Favourites — your usuals */}
                {favourites.length > 0 && (
                    <section className="px-5 mt-6">
                        <div className="flex items-baseline justify-between mb-3">
                            <h2 className="font-display text-xl font-black tracking-tight flex items-center gap-2">
                                <Star className="h-4 w-4 fill-[#FF4F18] text-[#FF4F18]" strokeWidth={2} />
                                Your usuals
                            </h2>
                            <span className="text-[10px] uppercase tracking-[0.2em] text-[#8C7A72] font-semibold">
                                Tap to reorder
                            </span>
                        </div>
                        <div className="flex gap-2 overflow-x-auto hide-scroll -mx-5 px-5 pb-2">
                            {favourites.map((fav) => (
                                <div
                                    key={fav.id}
                                    className="flex-shrink-0 w-56 bg-white rounded-2xl p-3 border border-[#E8DCCB]/70 relative"
                                    data-testid={`fav-${fav.id}`}
                                >
                                    <button
                                        data-testid={`fav-remove-${fav.id}`}
                                        onClick={() => removeFav(fav)}
                                        className="absolute top-2 right-2 h-6 w-6 rounded-full bg-[#F9F6F0] border border-[#E8DCCB] flex items-center justify-center opacity-70 hover:opacity-100"
                                    >
                                        <Trash2 className="h-3 w-3" strokeWidth={2.5} />
                                    </button>
                                    <div className="font-display font-bold text-sm leading-tight pr-6">
                                        {fav.name}
                                        {fav.size ? ` · ${fav.size === "L" ? "Lg" : "Sm"}` : ""}
                                    </div>
                                    {fav.label && (
                                        <div className="text-[11px] text-[#8C7A72] mt-1 leading-snug min-h-[14px]">
                                            {fav.label}
                                        </div>
                                    )}
                                    <div className="mt-2 flex items-center justify-between">
                                        <span className="font-display font-black text-base">
                                            ${fav.price.toFixed(2)}
                                        </span>
                                        <button
                                            data-testid={`fav-add-${fav.id}`}
                                            onClick={() => addFavToCart(fav)}
                                            className="px-3 py-1.5 rounded-full bg-[#FF4F18] text-white text-xs font-display font-bold active:scale-95"
                                        >
                                            Add
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Coffee section */}
                <section className="px-5 mt-8">
                    <div className="flex items-baseline justify-between mb-4">
                        <h2 className="font-display text-3xl font-black tracking-tight">
                            Coffee
                        </h2>
                        <span className="text-xs uppercase tracking-[0.2em] text-[#8C7A72] flex items-center gap-1.5">
                            <Coffee className="h-3.5 w-3.5" strokeWidth={2.5} /> Barista made
                        </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {menu.coffee.map((item, idx) => (
                            <motion.button
                                key={item.id}
                                data-testid={`coffee-${item.id}`}
                                onClick={() => openItem(item, "coffee")}
                                disabled={item.sold_out}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.04 }}
                                className={`bg-white rounded-3xl p-4 text-left border border-[#E8DCCB]/60 shadow-[0_4px_20px_rgba(44,26,18,0.03)] active:scale-[0.97] transition-transform relative overflow-hidden group ${
                                    item.sold_out ? "opacity-50 grayscale" : ""
                                }`}
                            >
                                <div className="absolute -top-2 -right-2 h-16 w-16 rounded-full bg-[#FF4F18]/10 group-hover:bg-[#FF4F18]/20 transition-colors" />
                                {item.image_url && (
                                    <img
                                        src={item.image_url}
                                        alt=""
                                        className="w-full h-24 object-cover rounded-2xl mb-3 relative"
                                        onError={(e) => (e.currentTarget.style.display = "none")}
                                    />
                                )}
                                <div className="font-display font-bold text-lg leading-tight relative">
                                    {item.name}
                                </div>
                                {item.sold_out && (
                                    <div
                                        data-testid={`sold-out-${item.id}`}
                                        className="mt-2 inline-block text-[10px] uppercase tracking-widest text-[#c73810] font-bold px-2 py-0.5 rounded-full bg-[#FF4F18]/10 border border-[#FF4F18]/30 relative"
                                    >
                                        Sold out
                                    </div>
                                )}
                                <div className="mt-3 flex items-end justify-between relative">
                                    <div className="text-xs text-[#8C7A72] font-medium">S · L</div>
                                    <div className="font-display font-black text-xl">
                                        ${item.sizes.S.toFixed(2)}
                                    </div>
                                </div>
                            </motion.button>
                        ))}
                    </div>
                </section>

                {/* Food section */}
                <section className="px-5 mt-10">
                    <div className="flex items-baseline justify-between mb-4">
                        <h2 className="font-display text-3xl font-black tracking-tight">Bites</h2>
                        <span className="text-xs uppercase tracking-[0.2em] text-[#8C7A72] flex items-center gap-1.5">
                            <Croissant className="h-3.5 w-3.5" strokeWidth={2.5} /> Fresh daily
                        </span>
                    </div>
                    <div className="flex flex-col gap-3">
                        {menu.food.map((item, idx) => (
                            <motion.button
                                key={item.id}
                                data-testid={`food-${item.id}`}
                                onClick={() => openItem(item, "food")}
                                disabled={item.sold_out}
                                initial={{ opacity: 0, x: -6 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.04 }}
                                className={`bg-white rounded-2xl p-4 border border-[#E8DCCB]/60 flex items-center justify-between gap-3 active:scale-[0.98] transition-transform ${
                                    item.sold_out ? "opacity-50 grayscale" : ""
                                }`}
                            >
                                {item.image_url && (
                                    <img
                                        src={item.image_url}
                                        alt=""
                                        className="h-14 w-14 rounded-xl object-cover shrink-0"
                                        onError={(e) => (e.currentTarget.style.display = "none")}
                                    />
                                )}
                                <div className="flex-1 text-left min-w-0">
                                    <div className="font-display font-bold text-base truncate">
                                        {item.name}
                                    </div>
                                    {item.sold_out && (
                                        <div className="mt-0.5 inline-block text-[10px] uppercase tracking-widest text-[#c73810] font-bold px-2 py-0.5 rounded-full bg-[#FF4F18]/10 border border-[#FF4F18]/30">
                                            Sold out
                                        </div>
                                    )}
                                </div>
                                <div className="font-display font-black text-lg shrink-0">
                                    ${item.price.toFixed(2)}
                                </div>
                            </motion.button>
                        ))}
                    </div>
                </section>

                {/* Cart floating CTA */}
                <AnimatePresence>
                    {count > 0 && (
                        <motion.button
                            key="cart-cta"
                            data-testid="view-cart-cta"
                            initial={{ y: 100, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 100, opacity: 0 }}
                            onClick={() => nav(`/${slug}/cart`)}
                            className="fixed bottom-5 left-1/2 -translate-x-1/2 w-[92%] max-w-[420px] bg-[#2C1A12] text-white rounded-full pl-5 pr-3 py-3 flex items-center justify-between shadow-[0_20px_40px_rgba(44,26,18,0.32)] z-30"
                        >
                            <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full bg-[#FF4F18] flex items-center justify-center relative">
                                    <ShoppingBag className="h-4 w-4" strokeWidth={2.5} />
                                    <span
                                        data-testid="cart-count-badge"
                                        className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-white text-[#2C1A12] text-[10px] font-black flex items-center justify-center"
                                    >
                                        {count}
                                    </span>
                                </div>
                                <span className="font-display font-bold">View cart</span>
                            </div>
                            <div className="bg-white/10 rounded-full px-4 py-1.5 font-display font-bold text-sm">
                                ${subtotal.toFixed(2)}
                            </div>
                        </motion.button>
                    )}
                </AnimatePresence>

                {/* Item sheet */}
                <AnimatePresence>
                    {selected && (
                        <>
                            <motion.div
                                key="scrim"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={closeSheet}
                                className="fixed inset-0 bg-[#2C1A12]/60 z-40"
                            />
                            <motion.div
                                key="sheet"
                                initial={{ y: "100%" }}
                                animate={{ y: 0 }}
                                exit={{ y: "100%" }}
                                transition={{ type: "spring", damping: 30, stiffness: 300 }}
                                className="fixed inset-x-0 bottom-0 mx-auto w-full max-w-md bg-[#F9F6F0] rounded-t-[32px] p-6 pb-8 z-50 max-h-[92vh] overflow-y-auto overflow-x-hidden"
                            >
                                <div className="flex justify-between items-start mb-6">
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
                                            {selected.kind === "coffee" ? "Coffee" : "Food"}
                                        </div>
                                        <div className="font-display text-3xl font-black mt-1">
                                            {selected.item.name}
                                        </div>
                                    </div>
                                    <button
                                        data-testid="close-sheet"
                                        onClick={closeSheet}
                                        className="h-10 w-10 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center"
                                    >
                                        <X className="h-4 w-4" strokeWidth={2.5} />
                                    </button>
                                </div>

                                {selected.kind === "coffee" && (
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold mb-2">
                                            Size
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            {["S", "L"].map((s) => (
                                                <button
                                                    key={s}
                                                    data-testid={`size-${s}`}
                                                    onClick={() => setSize(s)}
                                                    className={`py-4 rounded-2xl font-display font-bold text-lg border-2 transition-all ${
                                                        size === s
                                                            ? "border-[#FF4F18] bg-[#FF4F18] text-white"
                                                            : "border-[#E8DCCB] bg-white text-[#2C1A12]"
                                                    }`}
                                                >
                                                    <div>{sizeLabel[s]}</div>
                                                    <div className="text-xs font-medium mt-1 opacity-80">
                                                        ${selected.item.sizes[s].toFixed(2)}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selected.kind === "coffee" && (
                                    <>
                                        {/* Milk */}
                                        <div className="mt-6">
                                            <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold mb-2">
                                                Milk
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {MILK_OPTIONS.map((m) => (
                                                    <button
                                                        key={m.id}
                                                        data-testid={`milk-${m.id}`}
                                                        onClick={() =>
                                                            setCustom({ ...custom, milk: m.id })
                                                        }
                                                        className={`px-4 py-2.5 rounded-full font-display font-bold text-sm border-2 transition-all ${
                                                            custom.milk === m.id
                                                                ? "border-[#FF4F18] bg-[#FF4F18] text-white"
                                                                : "border-[#E8DCCB] bg-white text-[#2C1A12]"
                                                        }`}
                                                    >
                                                        {m.label}
                                                        {m.upcharge > 0 && (
                                                            <span className="ml-1 opacity-70 text-xs">
                                                                +${m.upcharge.toFixed(2)}
                                                            </span>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Sugar */}
                                        <div className="mt-6">
                                            <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold mb-2">
                                                Sugar
                                            </div>
                                            <div className="grid grid-cols-3 gap-2">
                                                {[0, 1, 2].map((n) => (
                                                    <button
                                                        key={n}
                                                        data-testid={`sugar-${n}`}
                                                        onClick={() =>
                                                            setCustom({ ...custom, sugar: n })
                                                        }
                                                        className={`py-3 rounded-2xl font-display font-bold text-sm border-2 transition-all ${
                                                            custom.sugar === n
                                                                ? "border-[#FF4F18] bg-[#FF4F18] text-white"
                                                                : "border-[#E8DCCB] bg-white text-[#2C1A12]"
                                                        }`}
                                                    >
                                                        {n === 0 ? "None" : n === 1 ? "1 sugar" : "2 sugars"}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Toggles */}
                                        <div className="mt-6 grid grid-cols-2 gap-2">
                                            <button
                                                data-testid="toggle-extra-hot"
                                                onClick={() =>
                                                    setCustom({
                                                        ...custom,
                                                        extra_hot: !custom.extra_hot,
                                                    })
                                                }
                                                className={`py-3 rounded-2xl font-display font-bold text-sm border-2 transition-all ${
                                                    custom.extra_hot
                                                        ? "border-[#FF4F18] bg-[#FF4F18] text-white"
                                                        : "border-[#E8DCCB] bg-white text-[#2C1A12]"
                                                }`}
                                            >
                                                Extra hot
                                            </button>
                                            <button
                                                data-testid="toggle-decaf"
                                                onClick={() =>
                                                    setCustom({ ...custom, decaf: !custom.decaf })
                                                }
                                                className={`py-3 rounded-2xl font-display font-bold text-sm border-2 transition-all ${
                                                    custom.decaf
                                                        ? "border-[#FF4F18] bg-[#FF4F18] text-white"
                                                        : "border-[#E8DCCB] bg-white text-[#2C1A12]"
                                                }`}
                                            >
                                                Decaf
                                            </button>
                                        </div>
                                    </>
                                )}

                                {/* Modifiers — extras like "Extra shot +$0.80" */}
                                {modifierList.length > 0 && (
                                    <div className="mt-6">
                                        <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold mb-2">
                                            Extras
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            {modifierList.map((m) => {
                                                const on = selectedMods.includes(m.id);
                                                return (
                                                    <button
                                                        key={m.id}
                                                        data-testid={`modifier-${m.id}`}
                                                        onClick={() =>
                                                            setSelectedMods(
                                                                on
                                                                    ? selectedMods.filter((x) => x !== m.id)
                                                                    : [...selectedMods, m.id]
                                                            )
                                                        }
                                                        className={`py-3 px-4 rounded-2xl font-display font-bold text-sm border-2 transition-all flex items-center justify-between ${
                                                            on
                                                                ? "border-[#FF4F18] bg-[#FF4F18] text-white"
                                                                : "border-[#E8DCCB] bg-white text-[#2C1A12]"
                                                        }`}
                                                    >
                                                        <span>{m.name}</span>
                                                        <span className={on ? "opacity-90" : "text-[#8C7A72]"}>
                                                            {Number(m.price_delta) > 0
                                                                ? `+$${Number(m.price_delta).toFixed(2)}`
                                                                : Number(m.price_delta) < 0
                                                                ? `-$${Math.abs(Number(m.price_delta)).toFixed(2)}`
                                                                : "Free"}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                <div className="mt-6">
                                    <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold mb-2">
                                        Quantity
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <button
                                            data-testid="qty-minus"
                                            onClick={() => setQty(Math.max(1, qty - 1))}
                                            className="h-12 w-12 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center active:scale-95"
                                        >
                                            <Minus className="h-4 w-4" strokeWidth={2.5} />
                                        </button>
                                        <div
                                            data-testid="qty-value"
                                            className="font-display font-black text-3xl w-12 text-center"
                                        >
                                            {qty}
                                        </div>
                                        <button
                                            data-testid="qty-plus"
                                            onClick={() => setQty(qty + 1)}
                                            className="h-12 w-12 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center active:scale-95"
                                        >
                                            <Plus className="h-4 w-4" strokeWidth={2.5} />
                                        </button>
                                    </div>
                                </div>

                                {/* Save as favourite */}
                                {phone && (
                                    <button
                                        data-testid="save-fav-toggle"
                                        onClick={() => setSaveFav(!saveFav)}
                                        className={`mt-6 w-full py-3 rounded-2xl font-display font-bold text-sm border-2 transition-all flex items-center justify-center gap-2 ${
                                            saveFav
                                                ? "border-[#FF4F18] bg-[#FF4F18]/10 text-[#FF4F18]"
                                                : "border-[#E8DCCB] bg-white text-[#2C1A12]"
                                        }`}
                                    >
                                        <Bookmark
                                            className={`h-4 w-4 ${saveFav ? "fill-[#FF4F18]" : ""}`}
                                            strokeWidth={2.5}
                                        />
                                        {saveFav ? "Will save to your usuals" : "Save to your usuals"}
                                    </button>
                                )}

                                <button
                                    data-testid="add-to-cart-btn"
                                    onClick={confirmAdd}
                                    className="mt-4 w-full py-5 rounded-full bg-[#FF4F18] text-white font-display font-bold text-lg active:scale-[0.98] shadow-[0_12px_32px_rgba(255,79,24,0.32)]"
                                >
                                    Add to cart · ${(currentPrice * qty).toFixed(2)}
                                </button>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>
            </div>
        </PhoneShell>
    );
};

export default Menu;
